import { createElement, type ReactNode } from "react";
import { useMutation, type DefaultError, type UseMutationOptions, type UseMutationResult } from "@tanstack/react-query";
import { toast } from "sonner";
import { microcopy, type MicrocopyKey } from "./microcopy";
import { confirmAnswer, isReportable, refusedStep, saveFailure, type SaveCause, type SaveExpectation } from "./save-core";

export { SaveError, confirmAnswer, refusedStep, saveFailure, type SaveCause, type SaveExpectation, type SaveFailure } from "./save-core";

/**
 * The one way the back office and the portal confirm or report a change (S 2.8). Every mutation goes through `useSave` and every success
 * message through it or `notifySaved`; scripts/dev/test/save-feedback.test.mjs fails the build on a `useMutation` or a `toast.success`
 * anywhere else in those apps.
 *
 *   const save = useSave({ mutationFn: (body) => api.put<Note>(url, body), saved: t("…saved"), onSuccess: () => invalidate() });
 *
 * - "Saved" is shown only after the API answered 2xx with its own answer – the stored record, or its "no content" for actions that return
 *   nothing (`expect: "record"` insists on the record) – and that answer does not say the change was refused (`succeeded: false`).
 * - A failure is reported with its cause – offline, the server unreachable, the session ended, no permission, the record changed or gone,
 *   the data refused (with the server's own words), the server failing, an answer that does not confirm the save – under `failed` (default
 *   "Not saved"). `report: "silent"` leaves the report to the screen, which reads it with `saveFailureText(error)`.
 * - A stored change whose answer says the step it was to take was refused (a task completed, its workflow step refused) is confirmed as
 *   saved, and the refused step is reported beside it with the server's reason ("The file did not move to the next step").
 */

export type SavedMessage = string | { title: string; description?: ReactNode; duration?: number; action?: { label: string; onClick: () => void } };

export type SaveOptions<TData, TError, TVariables, TOnMutateResult> = Omit<UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, "mutationFn"> & {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** The confirmation once the change is stored: a message, `true` for "Saved", or a function of the stored answer; none when omitted. */
  saved?: SavedMessage | true | null | ((data: TData, variables: TVariables) => SavedMessage | true | null | undefined);
  /** The heading of the failure message (the cause and the server's words follow it). */
  failed?: string | ((error: TError, variables: TVariables) => string);
  /** `silent` when the screen shows the failure itself. */
  report?: "toast" | "silent";
  /** What a successful answer must carry (see `confirmAnswer`); `answer` by default. */
  expect?: SaveExpectation;
};

const CAUSE_TEXT: Record<Exclude<SaveCause, "cancelled">, MicrocopyKey> = {
  offline: "saveOffline",
  network: "saveNetwork",
  session: "saveSession",
  forbidden: "saveForbidden",
  notFound: "saveNotFound",
  conflict: "saveConflict",
  invalid: "saveInvalid",
  tooLarge: "saveTooLarge",
  busy: "saveBusy",
  server: "saveServer",
  unconfirmed: "saveUnconfirmed",
  refused: "saveRefused",
};

function arabic(): boolean {
  return typeof document !== "undefined" && document.documentElement.lang.toLowerCase().startsWith("ar");
}

/**
 * The failure in words for the reader: the cause, then the server's own words when it gave any. Empty for a request the screen cancelled.
 * `locale` asks for one language (a text kept in both, such as the agentic review's); the page's language otherwise.
 */
export function saveFailureText(error: unknown, locale?: "en" | "ar"): string {
  const f = saveFailure(error, locale ? locale === "ar" : arabic());
  if (!isReportable(f)) return "";
  const cause = microcopy(CAUSE_TEXT[f.cause as Exclude<SaveCause, "cancelled">], locale);
  return f.detail ? `${cause} ${f.detail}` : cause;
}

/** Reports a failed change with its cause (for flows that call the API outside `useSave`, e.g. a sequence of uploads). */
export function notifySaveFailed(error: unknown, title?: string): void {
  const f = saveFailure(error, arabic());
  if (!isReportable(f)) return;
  const cause = microcopy(CAUSE_TEXT[f.cause as Exclude<SaveCause, "cancelled">]);
  const description = f.detail
    ? createElement("span", { className: "block" }, createElement("span", { className: "block" }, cause), createElement("span", { className: "mt-0.5 block" }, f.detail))
    : cause;
  toast.error(title ?? microcopy("saveFailed"), { description });
}

/** Answers whose refused step was reported already: the hook and a per-call `notifySaved` see the same answer, and it is told once. */
const toldSteps = new WeakSet<object>();

/** Reports the step a stored change was refused, with the server's reason, beside the change's confirmation (see `refusedStep`). */
function reportRefusedStep(answer: unknown): void {
  const step = refusedStep(answer);
  if (!step || toldSteps.has(answer as object)) return;
  toldSteps.add(answer as object);
  toast.error(microcopy("stepRefused"), { description: step.reason ?? microcopy("saveRefused") });
}

function show(message: SavedMessage | true): void {
  if (message === true) toast.success(microcopy("saved"));
  else if (typeof message === "string") toast.success(message);
  else toast.success(message.title, { description: message.description, duration: message.duration, action: message.action });
}

/**
 * Confirms a change outside `useSave` (a per-call `mutate(…, { onSuccess })`, an awaited call): the answer is checked first, and a failure is
 * reported instead of the confirmation when it does not confirm the save. Returns whether the change was confirmed.
 */
export function notifySaved(answer: unknown, message: SavedMessage | true, expect: SaveExpectation = "answer"): boolean {
  try {
    confirmAnswer(answer, expect);
  } catch (e) {
    notifySaveFailed(e);
    return false;
  }
  show(message);
  reportRefusedStep(answer);
  return true;
}

/**
 * A confirmation of an action that stores no record – a link copied, a file downloaded, a check run, the session extended. A change to
 * data is confirmed by `useSave` or `notifySaved`, never by this.
 */
export function notifyDone(message: string): void {
  toast.success(message);
}

/** Copies a text for the person and confirms it, or says it could not be copied (the browser refused the clipboard). */
export async function copyText(value: string, message: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    toast.error(microcopy("copyFailed"));
    return false;
  }
  notifyDone(message);
  return true;
}

export function useSave<TData = unknown, TError = DefaultError, TVariables = void, TOnMutateResult = unknown>(
  options: SaveOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const { saved, failed, report = "toast", expect = "answer", mutationFn, onSuccess, onError, ...rest } = options;
  return useMutation<TData, TError, TVariables, TOnMutateResult>({
    ...rest,
    mutationFn: async (variables) => confirmAnswer(await mutationFn(variables), expect),
    onSuccess: async (data, variables, onMutateResult, context) => {
      // The change is stored at this point: a fault in the screen's own follow-up (a refresh, a navigation) must not report it as not saved.
      try { await onSuccess?.(data, variables, onMutateResult, context); } catch (e) { console.error(e); }
      const message = typeof saved === "function" ? saved(data, variables) : saved;
      if (message) show(message);
      reportRefusedStep(data);
    },
    onError: async (error, variables, onMutateResult, context) => {
      if (report === "toast") notifySaveFailed(error, typeof failed === "function" ? failed(error, variables) : failed);
      await onError?.(error, variables, onMutateResult, context);
    },
  });
}
